import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { QrCode, MessageCircle, CreditCard, CheckCircle2, Copy, Phone, User, Upload, Building2, Receipt, ChevronRight, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { orders as ordersApi, razorpay as razorpayApi } from '../lib/api';
import { useCustomer } from '../lib/customerContext';

const buildWhatsAppUrl = (phone, message) => {
  const digits = (phone || '').replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

const orderMessage = ({ brand, buyer, salesRep, method, items, totalQty, price, subtotal, gstRate, gstAmount, mdrAmount, grandTotal, extraNote }) => {
  const lines = [
    `*New Order — ${brand}*`,
    '',
    `👤 Name: ${buyer.name}`,
    `📱 Phone: ${buyer.phone}`,
    `📧 Email: ${buyer.email}`,
    `📍 Delivery: ${buyer.address}`,
    buyer.gst ? `🧾 GST: ${buyer.gst}` : `🧾 GST: (not provided)`,
    `🧑‍💼 Sale by: ${salesRep || 'self'}`,
    `💳 Payment: ${method}`,
    '',
    '*Items:*',
    ...items.map((i) => `• ${i.product ? `${i.product} — ` : ''}${i.color} / ${i.size} — ${i.qty} pcs`),
    '',
    `Qty: ${totalQty} pcs`,
    `Rate: ₹${price}/pc`,
    `Subtotal: ₹${subtotal.toLocaleString('en-IN')}`,
    `GST (${gstRate}%): ₹${gstAmount.toLocaleString('en-IN')}`,
    ...(mdrAmount ? [`Payment charges (MDR 0.5%): ₹${mdrAmount.toLocaleString('en-IN')}`] : []),
    `*Grand Total: ₹${grandTotal.toLocaleString('en-IN')}*`,
    '',
    '🚚 Shipping charges excluded. Team will coordinate for courier after order.',
  ];
  if (extraNote) lines.push('', extraNote);
  return lines.join('\n');
};

export const PaymentModal = ({ open, onClose, onOrderSent, branding, items, totalQty, price, total, productName }) => {
  const [step, setStep] = useState('form'); // form | choose | qr | bank | razorpay | wa_direct
  const [buyer, setBuyer] = useState({ name: '', phone: '', email: '', address: '', gst: '' });
  const [salesRep, setSalesRep] = useState(branding?.default_sales_rep || 'self');
  const [notes, setNotes] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [rzProcessing, setRzProcessing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [waUrl, setWaUrl] = useState('');
  const { customer } = useCustomer();

  React.useEffect(() => {
    if (open) {
      setStep('form');
      setSalesRep(branding?.default_sales_rep || 'self');
      setScreenshot('');
      setConfirmed(false);
      setOrderId(null);
      if (customer) {
        setBuyer({
          name: customer.name || '',
          phone: customer.phone || '',
          email: customer.email || '',
          address: [customer.address, customer.pincode].filter(Boolean).join(', '),
          gst: customer.gst_number || '',
        });
      }
    }
  }, [open, branding, customer]);

  const gstRate = Number(branding?.gst_rate ?? 5);
  const subtotal = total;
  const gstAmount = Math.round((subtotal * gstRate) / 100);
  const grandTotal = subtotal + gstAmount;
  const MDR_RATE = 0.5;
  const mdrAmount = Math.round((grandTotal * MDR_RATE) / 100);
  const totalWithMdr = grandTotal + mdrAmount;
  // MDR (gateway fee) applies only to online gateway payments (UPI QR + Razorpay), never bank transfer
  const stepHasMdr = step === 'qr' || step === 'razorpay';
  const payableTotal = stepHasMdr ? totalWithMdr : grandTotal;

  const waNumber = branding?.whatsapp_number || '918882480204';
  const brandName = branding?.name || 'Store';

  const validateBuyer = () => {
    if (!buyer.name.trim()) return 'Name is required';
    if (!buyer.phone.trim() || buyer.phone.replace(/\D/g, '').length < 10) return 'Valid phone is required';
    if (!buyer.email.trim() || !buyer.email.includes('@')) return 'Valid email is required';
    if (!buyer.address.trim() || buyer.address.length < 8) return 'Delivery address is required';
    return null;
  };

  const uploadScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setScreenshot(reader.result); toast.success('Screenshot attached'); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Build the WhatsApp order URL (and persist order as pending). Does NOT open the window.
  const buildOrderWaUrl = (method, extra = '', paymentId = '', payable = grandTotal, mdr = 0) => {
    markPending(method, paymentId, payable, mdr);
    const bits = [];
    if (screenshot) bits.push('📷 Payment screenshot ready — please attach on WhatsApp using 📎');
    if (extra) bits.push(extra);
    if (notes) bits.push(`📝 Note: ${notes}`);
    const msg = orderMessage({
      brand: brandName, buyer, salesRep, method, items,
      totalQty, price, subtotal, gstRate, gstAmount, mdrAmount: mdr, grandTotal: payable,
      extraNote: bits.join('\n'),
    });
    return buildWhatsAppUrl(waNumber, msg);
  };

  const sendOnWhatsApp = (method, extra = '', paymentId = '', payable = grandTotal, mdr = 0) => {
    const err = validateBuyer();
    if (err) { toast.error(err); return; }
    const url = buildOrderWaUrl(method, extra, paymentId, payable, mdr);
    window.open(url, '_blank');
    toast.success('Opening WhatsApp with your order');
    if (onOrderSent) onOrderSent();
    setTimeout(onClose, 500);
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied'); };

  const orderPayload = (status) => ({
    product_name: productName || '',
    buyer_name: buyer.name,
    buyer_phone: buyer.phone,
    buyer_email: buyer.email,
    buyer_address: buyer.address,
    buyer_gst: buyer.gst,
    items: (items || []).map((i) => ({ product: i.product || productName || '', color: i.color, size: i.size, qty: i.qty })),
    total_qty: totalQty,
    price,
    subtotal,
    gst_rate: gstRate,
    gst_amount: gstAmount,
    grand_total: grandTotal,
    sales_rep: salesRep || 'self',
    notes,
    customization: notes,
    customer_id: customer?.id || '',
    status,
  });

  // Create an 'upcoming' order (buyer entered details but not paid yet)
  const createUpcomingOrder = async () => {
    try {
      const doc = await ordersApi.create(orderPayload('upcoming'));
      setOrderId(doc.id);
      return doc.id;
    } catch (e) {
      return null;
    }
  };

  // Move the order to 'pending' once buyer places it (sends on WhatsApp / pays)
  const markPending = async (method, paymentId = '', payable = grandTotal, mdr = 0) => {
    try {
      let id = orderId;
      if (!id) id = await createUpcomingOrder();
      if (id) await ordersApi.updatePublic(id, { status: 'pending', payment_method: method, payment_id: paymentId, notes, grand_total: payable, mdr_amount: mdr });
    } catch (e) { console.warn('Order persistence failed:', e); }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (typeof window.Razorpay !== 'undefined') { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const openRazorpay = async () => {
    const err = validateBuyer();
    if (err) { toast.error(err); return; }
    setRzProcessing(true);
    try {
      // ensure our internal order exists (status 'upcoming')
      let id = orderId;
      if (!id) id = await createUpcomingOrder();

      const ok = await loadRazorpayScript();
      if (!ok) { setRzProcessing(false); toast.error('Failed to load Razorpay'); return; }

      // backend creates the Razorpay order (secure) and returns the public key id
      const rz = await razorpayApi.createOrder(totalWithMdr, id);

      const options = {
        key: rz.key_id,
        order_id: rz.rz_order_id,
        amount: rz.amount,
        currency: rz.currency,
        name: brandName,
        description: `Order of ${totalQty} pcs`,
        handler: async (resp) => {
          try {
            await razorpayApi.verify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              order_id: id,
            });
            setRzProcessing(false);
            setConfirmed(true);
            toast.success('Payment verified!');
            if (onOrderSent) onOrderSent();
            const url = buildOrderWaUrl(`Razorpay (PAID) · Payment ID: ${resp.razorpay_payment_id}`, `✅ Payment verified via Razorpay. Payment ID: ${resp.razorpay_payment_id}`, resp.razorpay_payment_id, totalWithMdr, mdrAmount);
            setWaUrl(url);
            setStep('success');
            // Best-effort auto-open (may be blocked by popup blocker since this is post-payment, not a direct tap)
            try { window.open(url, '_blank'); } catch (_) { /* button fallback below */ }
          } catch (e) {
            setRzProcessing(false);
            toast.error('Payment could not be verified. Contact us with your payment ID.');
          }
        },
        modal: { ondismiss: () => setRzProcessing(false) },
        prefill: { name: buyer.name, contact: buyer.phone, email: buyer.email },
        theme: { color: '#f97316' },
      };
      const rzCheckout = new window.Razorpay(options);
      rzCheckout.on('payment.failed', () => { setRzProcessing(false); toast.error('Payment failed. Please try again.'); });
      rzCheckout.open();
    } catch (e) {
      setRzProcessing(false);
      const msg = e.response?.status === 503 ? 'Razorpay not configured on server.' : 'Could not start Razorpay payment.';
      toast.error(msg);
    }
  };

  const BillCard = () => (
    <div className="rounded-md bg-orange-50 border border-orange-100 p-3 mb-3 space-y-0.5 text-[12px]">
      <div className="flex justify-between text-neutral-700"><span>Qty</span><span className="font-medium">{totalQty} pcs @ ₹{price}</span></div>
      <div className="flex justify-between text-neutral-700"><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
      <div className="flex justify-between text-neutral-700"><span>GST ({gstRate}%)</span><span>₹{gstAmount.toLocaleString('en-IN')}</span></div>
      {stepHasMdr && (
        <div data-testid="mdr-charge-line" className="flex justify-between text-neutral-700"><span>Payment charges (MDR 0.5%)</span><span>₹{mdrAmount.toLocaleString('en-IN')}</span></div>
      )}
      <div className="flex justify-between text-[15px] pt-1 border-t border-orange-200 mt-1"><span className="font-semibold">Grand Total</span><span className="font-bold text-orange-700">₹{payableTotal.toLocaleString('en-IN')}</span></div>
      {stepHasMdr && (
        <div data-testid="mdr-explain-note" className="mt-2 pt-2 border-t border-dashed border-orange-200 text-[11px] text-neutral-600">
          Online payments par <b>0.5% MDR (Merchant Discount Rate)</b> charge lagta hai — yeh payment gateway/bank ki fees hai, hum extra kuch nahi lete. Isse bachne ke liye <b>Bank Transfer (NEFT/IMPS)</b> choose karein — uspar koi extra charge nahi.
        </div>
      )}
      {step === 'bank' && (
        <div data-testid="bank-no-charge-note" className="mt-2 pt-2 border-t border-dashed border-orange-200 text-[11px] text-emerald-700 font-medium">
          ✓ Bank Transfer par koi extra charge nahi — 0.5% MDR sirf online (QR/Razorpay) payments par lagta hai.
        </div>
      )}
      <div data-testid="shipping-exclusion-note" className="flex items-start gap-1.5 mt-2 pt-2 border-t border-dashed border-orange-200 text-[11px] text-neutral-600">
        <Truck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-500" />
        <span><b>Shipping charges excluded.</b> Order place karne ke baad hamari team courier ke liye aapse coordinate karegi.</span>
      </div>
    </div>
  );

  const ScreenshotUploader = () => (
    <div className="rounded-lg border border-dashed border-neutral-300 p-3 mb-3 bg-neutral-50">
      <Label className="text-[12px] font-semibold text-neutral-800">Attach payment screenshot (recommended)</Label>
      <div className="flex items-center gap-2 mt-1">
        {screenshot && <img src={screenshot} alt="" className="w-14 h-14 object-cover rounded border border-neutral-200" />}
        <label className="cursor-pointer inline-flex items-center gap-1 px-2.5 py-1.5 text-[12px] bg-white border border-neutral-300 rounded hover:border-orange-500">
          <Upload className="w-3.5 h-3.5" />
          {screenshot ? 'Change' : 'Attach screenshot'}
          <input type="file" accept="image/*" className="hidden" onChange={uploadScreenshot} />
        </label>
        {screenshot && <button onClick={() => setScreenshot('')} className="text-[11px] text-red-500 hover:text-red-700">Remove</button>}
      </div>
      <p className="text-[10px] text-neutral-500 mt-1">Also share this screenshot on WhatsApp using 📎 for fast verification.</p>
    </div>
  );

  const ConfirmCheckbox = () => (
    <label className="flex items-start gap-2 text-[12px] cursor-pointer mb-2 select-none">
      <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 accent-green-600" />
      <span className="text-neutral-700">I have made the payment of <b>₹{payableTotal.toLocaleString('en-IN')}</b> and I will share the screenshot on WhatsApp.</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-neutral-100">
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-500" />
            {step === 'form' && 'Checkout — Delivery Details'}
            {step === 'choose' && 'Choose Payment Method'}
            {step === 'qr' && 'Pay via UPI QR'}
            {step === 'bank' && 'Bank Transfer'}
            {step === 'razorpay' && 'Pay via Razorpay'}
            {step === 'wa_direct' && 'Order on WhatsApp'}
            {step === 'success' && 'Payment Successful'}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {step !== 'success' && <BillCard />}

          {step === 'form' && (
            <>
              <div className="space-y-2">
                <div>
                  <Label className="text-[11px]">Full Name <span className="text-red-500">*</span></Label>
                  <Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} placeholder="Rahul Kumar" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Phone <span className="text-red-500">*</span></Label><Input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} placeholder="98xxxxxxxx" /></div>
                  <div><Label className="text-[11px]">Email <span className="text-red-500">*</span></Label><Input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} placeholder="you@example.com" /></div>
                </div>
                <div>
                  <Label className="text-[11px]">Delivery Address <span className="text-red-500">*</span></Label>
                  <Textarea rows={2} value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} placeholder="House / Shop, Street, City, State, Pincode" />
                </div>
                <div>
                  <Label className="text-[11px]">GST Number <span className="text-neutral-400">(optional)</span></Label>
                  <Input value={buyer.gst} onChange={(e) => setBuyer({ ...buyer, gst: e.target.value.toUpperCase() })} placeholder="22AAAAA0000A1Z5" />
                </div>
                <div>
                  <Label className="text-[11px] flex items-center gap-1"><User className="w-3 h-3" /> Sale by (rep code)</Label>
                  <Input value={salesRep} onChange={(e) => setSalesRep(e.target.value)} placeholder="self / rep-name" />
                </div>
                <div>
                  <Label className="text-[11px]">Notes (optional)</Label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Preferred courier, delivery time, etc." />
                </div>
              </div>
              <Button onClick={() => { const err = validateBuyer(); if (err) { toast.error(err); } else { createUpcomingOrder(); setStep('choose'); } }} className="w-full mt-3 bg-orange-500 hover:bg-orange-600">
                Continue to Payment ❯
              </Button>
            </>
          )}

          {step === 'choose' && (
            <div className="space-y-2">
              <button onClick={() => setStep('qr')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 hover:border-emerald-500 hover:bg-emerald-50/40 text-left transition-colors">
                <div className="w-10 h-10 rounded-md bg-emerald-100 grid place-items-center"><QrCode className="w-5 h-5 text-emerald-700" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-[14px]">Pay via UPI QR</div>
                  <div className="text-[11px] text-neutral-600">Scan QR from any UPI app · Instant <span className="text-amber-600 font-medium">· +0.5% MDR</span></div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </button>
              <button onClick={() => setStep('bank')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 hover:border-blue-500 hover:bg-blue-50/40 text-left transition-colors">
                <div className="w-10 h-10 rounded-md bg-blue-100 grid place-items-center"><Building2 className="w-5 h-5 text-blue-700" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-[14px]">Bank Transfer (NEFT / IMPS / RTGS)</div>
                  <div className="text-[11px] text-neutral-600">Direct transfer to our account <span className="text-emerald-600 font-medium">· No extra charge</span></div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </button>
              <button onClick={() => setStep('razorpay')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 hover:border-purple-500 hover:bg-purple-50/40 text-left transition-colors">
                <div className="w-10 h-10 rounded-md bg-purple-100 grid place-items-center"><CreditCard className="w-5 h-5 text-purple-700" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-[14px]">Razorpay (Card / UPI / Net Banking)</div>
                  <div className="text-[11px] text-neutral-600">Secure online checkout · Auto-confirms on WhatsApp <span className="text-amber-600 font-medium">· +0.5% MDR</span></div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </button>
              <button onClick={() => setStep('wa_direct')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 hover:border-green-500 hover:bg-green-50/40 text-left transition-colors">
                <div className="w-10 h-10 rounded-md bg-green-100 grid place-items-center"><MessageCircle className="w-5 h-5 text-green-700" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-[14px]">Order on WhatsApp first</div>
                  <div className="text-[11px] text-neutral-600">Send order, pay after we confirm</div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </button>
              <button onClick={() => setStep('form')} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">← Back to details</button>
            </div>
          )}

          {step === 'qr' && (
            <>
              {branding?.upi_qr_url ? (
                <div className="rounded-lg border border-neutral-200 p-3 bg-white mb-3">
                  <div className="grid grid-cols-[auto,1fr] gap-3 items-center">
                    <img src={branding.upi_qr_url} alt="UPI QR" className="w-40 h-40 object-contain bg-white border border-neutral-200 rounded" />
                    <div className="text-[12px] text-neutral-700 space-y-1">
                      <div>Scan with any UPI app</div>
                      {branding.upi_id && (
                        <button onClick={() => copy(branding.upi_id)} className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800">
                          <Copy className="w-3 h-3" /> {branding.upi_id}
                        </button>
                      )}
                      <div className="font-semibold text-orange-700 mt-1">Amount: ₹{payableTotal.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-neutral-300 p-4 text-center text-[13px] text-neutral-500 mb-3">
                  Admin has not uploaded a UPI QR yet.
                </div>
              )}
              <ScreenshotUploader />
              <ConfirmCheckbox />
              <Button disabled={!confirmed} onClick={() => sendOnWhatsApp(`UPI QR · ₹${totalWithMdr}`, '', '', totalWithMdr, mdrAmount)} className="w-full bg-green-600 hover:bg-green-700 disabled:bg-neutral-300">
                <CheckCircle2 className="w-4 h-4 mr-1" /> I have paid — Send Order on WhatsApp
              </Button>
              <button onClick={() => setStep('choose')} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">← Back</button>
            </>
          )}

          {step === 'bank' && (
            <>
              <div className="rounded-lg border border-neutral-200 p-3 bg-white mb-3">
                <div className="text-[12px] space-y-1.5 text-neutral-700">
                  <div className="flex justify-between"><span className="text-neutral-500">Account Name</span><button onClick={() => copy(branding?.bank_account_name || 'RK TRADERS')} className="font-medium text-neutral-900 hover:text-blue-700 flex items-center gap-1">{branding?.bank_account_name || 'RK TRADERS'} <Copy className="w-3 h-3" /></button></div>
                  <div className="flex justify-between"><span className="text-neutral-500">Account No.</span><button onClick={() => copy(branding?.bank_account_number || '3979000100165921')} className="font-mono font-medium text-neutral-900 hover:text-blue-700 flex items-center gap-1">{branding?.bank_account_number || '3979000100165921'} <Copy className="w-3 h-3" /></button></div>
                  <div className="flex justify-between"><span className="text-neutral-500">IFSC</span><button onClick={() => copy(branding?.bank_ifsc || 'AUBL0002101')} className="font-mono font-medium text-neutral-900 hover:text-blue-700 flex items-center gap-1">{branding?.bank_ifsc || 'AUBL0002101'} <Copy className="w-3 h-3" /></button></div>
                  {branding?.bank_name && <div className="flex justify-between"><span className="text-neutral-500">Bank</span><span className="text-neutral-900">{branding.bank_name}</span></div>}
                  <div className="flex justify-between pt-1 border-t border-neutral-100"><span className="text-neutral-500">Amount</span><span className="font-bold text-orange-700">₹{grandTotal.toLocaleString('en-IN')}</span></div>
                </div>
              </div>
              <ScreenshotUploader />
              <ConfirmCheckbox />
              <Button disabled={!confirmed} onClick={() => sendOnWhatsApp(`Bank Transfer · ₹${grandTotal}`, '', '', grandTotal, 0)} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300">
                <CheckCircle2 className="w-4 h-4 mr-1" /> I have transferred — Send Order on WhatsApp
              </Button>
              <button onClick={() => setStep('choose')} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">← Back</button>
            </>
          )}

          {step === 'razorpay' && (
            <>
              <div className="rounded-lg bg-purple-50 border border-purple-100 p-3 text-[12px] text-purple-900 mb-3">
                Secure checkout: Card, UPI, Wallet, Net Banking. Payment ke baad ek tap me order WhatsApp par confirm ho jaayega.
                <div className="mt-1.5 text-[11px] text-purple-800/80">Note: Is amount me <b>0.5% MDR charge</b> (gateway fees) included hai. Bina charge ke pay karna ho to Bank Transfer choose karein.</div>
              </div>
              <Button data-testid="razorpay-pay-button" disabled={rzProcessing} onClick={openRazorpay} className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-neutral-300">
                <CreditCard className="w-4 h-4 mr-1" /> {rzProcessing ? 'Opening Razorpay...' : `Pay ₹${payableTotal.toLocaleString('en-IN')} via Razorpay`}
              </Button>
              <button onClick={() => setStep('choose')} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">← Back</button>
            </>
          )}

          {step === 'wa_direct' && (
            <>
              <div className="rounded-lg bg-green-50 border border-green-100 p-3 text-[12px] text-green-900 flex items-center gap-2 mb-3">
                <Phone className="w-4 h-4" /> Order sends to <b>+{waNumber}</b>. Pay after our confirmation.
              </div>
              <Button onClick={() => sendOnWhatsApp('WhatsApp direct (pay after confirmation)')} className="w-full bg-green-600 hover:bg-green-700">
                <MessageCircle className="w-4 h-4 mr-1" /> Send Order on WhatsApp
              </Button>
              <button onClick={() => setStep('choose')} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">← Back</button>
            </>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 grid place-items-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-[16px] font-semibold text-neutral-900">Payment Successful 🎉</h3>
              <p className="text-[12px] text-neutral-600 mt-1 mb-4">Aapka payment ho gaya aur order place ho gaya. Ab apne order ki detail WhatsApp par bhej kar confirm kar dein.</p>
              <Button
                data-testid="razorpay-whatsapp-button"
                onClick={() => { if (waUrl) window.open(waUrl, '_blank'); if (onOrderSent) onOrderSent(); setTimeout(onClose, 400); }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="w-4 h-4 mr-1" /> Send Order Details on WhatsApp
              </Button>
              <button onClick={onClose} className="w-full mt-2 text-[12px] text-neutral-500 hover:text-neutral-700">Done</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
